import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import { Button, FabList, HookFormCheckbox } from "components";
import { FormContainer, FormContent } from "components/layout";
import { isPartnerTypeInsideSales } from "helpers";
import { useGetMerchantFeesQuery } from "queries";
import { uiActions } from "store";
import {
	BackEndPlatformTypes,
	Processors,
	TerminalGatewayTypes,
	TerminalStatusTypes,
} from "types";
import {
	isValidAmountToBeBilledValidator,
	isValidFrontEndPlatformValidator,
} from "validators";

import {
	Origin,
	Program,
	TerminalInfo,
	Gateway,
	Terminal,
	PointOfSale,
	FileBuildInfo,
	InstallationInfo,
	InstallationContact,
	AdditionalInfo,
	ZeroCost,
} from "./fields";

import { ReactComponent as CheckIcon } from "static/images/checked.svg";
import { ReactComponent as SaveIcon } from "static/images/save.svg";
import { ReactComponent as SubmitIcon } from "static/images/submit.svg";
import styles from "./terminalSolution.module.scss";

const TERMINAL_SOLUTION_NAMES = [
	"isFileBuildOnly",
	"isTerminal",
	"isGateway",
	"isPointOfSale",
];

const FILE_BUILD_SECTION_NAME = "fileBuild";

type TerminalSolutionFormProps = {
	isLoading: boolean;
	defaultValues: any;
	processor: { id: number; name: string };
	onSubmit: any;
	onSubmitStageOnlyVar?: any;
	merchantInfo: any;
	fileBuild?: any;
	refetchTerminalData?: Function | undefined;
	boardArise?: ({ merchantId }: { merchantId: string }) => void;
	showMarketingServices?: boolean;
};

const TerminalSolutionForm = ({
	isLoading = false,
	defaultValues,
	processor,
	onSubmit,
	merchantInfo,
	fileBuild,
	refetchTerminalData,
	onSubmitStageOnlyVar,
	boardArise,
}: TerminalSolutionFormProps) => {
	const dispatch = useDispatch();

	const { isFetching: isMerchantFeesLoading, data: merchantFees } =
		useGetMerchantFeesQuery({
			merchantId: merchantInfo?.id,
		});

	const isDataLoading = isLoading || isMerchantFeesLoading;
	const formMethods = useForm({
		mode: "onBlur",
		reValidateMode: "onChange",
		shouldUnregister: true,
		defaultValues,
	});

	const {
		formState: { isDirty },
		getValues,
		setValue,
		handleSubmit,
		register,
		watch,
		reset,
	} = formMethods;

	const isTerminal = watch("isTerminal");
	const isGateway = watch("isGateway");
	const isPointOfSale = watch("isPointOfSale");
	const backEndPlatformType = watch("backEndPlatform");

	const isAriseBoardingAvailable =
		!isDirty &&
		typeof boardArise === "function" &&
		defaultValues?.gateway?.gatewayName?.name ===
			TerminalGatewayTypes.ARISE.name &&
		defaultValues?.backEndPlatform === BackEndPlatformTypes.Tsys.name &&
		defaultValues?.status === TerminalStatusTypes.Complete.name &&
		merchantInfo?.hasOwnProperty("ariseMerchantId") &&
		!merchantInfo?.ariseMerchantId;

	const isInsideSales = isPartnerTypeInsideSales(
		merchantInfo?.agent?.partnerType,
	);

	const [isCreationStageOnlyVar, setIsCreationStageOnlyVar] = useState(false);

	useEffect(() => {
		if (!isDataLoading) {
			reset(defaultValues);
		}
	}, [defaultValues, isDataLoading, reset]);

	const isValidFrontEndPlatform = useCallback(
		values => isValidFrontEndPlatformValidator({ values, dispatch }),
		[dispatch],
	);

	const isValidTerminalSolutionsType = useCallback(
		values => {
			let checkedCount = 0;
			TERMINAL_SOLUTION_NAMES.forEach(name => {
				values[name] && checkedCount++;
			});

			if (checkedCount !== 1) {
				dispatch(
					uiActions.showErrorNotification({
						title: "One of the options File build only (no equipment identified), terminal, gateway or POS must be selected.",
					}),
				);
				return false;
			}

			return true;
		},
		[dispatch],
	);

	const isValidAmountToBeBilled = useCallback(
		values =>
			values.isTerminal
				? isValidAmountToBeBilledValidator({
						values,
						dispatch,
				  })
				: true,
		[dispatch],
	);

	const validateUpdateOrComplete = useCallback(
		values => {
			if (!isValidTerminalSolutionsType(values)) {
				return false;
			}
			if (!isValidFrontEndPlatform(values)) {
				return false;
			}
			if (!isValidAmountToBeBilled(values)) {
				return false;
			}
			return true;
		},
		[
			isValidAmountToBeBilled,
			isValidFrontEndPlatform,
			isValidTerminalSolutionsType,
		],
	);

	const onSubmitDraft = async () => {
		const values = getValues();

		values.status = TerminalStatusTypes.Draft.name;

		if (!isValidTerminalSolutionsType(values)) {
			return;
		}
		if (!isValidAmountToBeBilled(values)) {
			return false;
		}
		onSubmit(values);
	};

	const onUpdateOrComplete = useCallback(
		async values => {
			values.status = TerminalStatusTypes.Complete.name;
			if (!validateUpdateOrComplete(values)) {
				return;
			}
			onSubmit(values);
		},
		[onSubmit, validateUpdateOrComplete],
	);

	const handleSubmitStageOnlyVar = useCallback(async () => {
		const values = getValues();

		if (!isValidTerminalSolutionsType(values)) return;
		values.status =
			values.status === TerminalStatusTypes.Complete.name
				? TerminalStatusTypes.Complete.name
				: TerminalStatusTypes.Draft.name;

		await onSubmitStageOnlyVar(values);
	}, [getValues, isValidTerminalSolutionsType, onSubmitStageOnlyVar]);

	const checkboxChangeHandler = useCallback(
		e => {
			if (e.currentTarget.checked) {
				TERMINAL_SOLUTION_NAMES.forEach(name => {
					if (name !== e.currentTarget.name) {
						setValue(name, false);
					}
				});
			}
		},
		[setValue],
	);

	const handleAriseBoarding = useCallback(() => {
		boardArise?.({
			merchantId: merchantInfo?.id,
		});
	}, [boardArise, merchantInfo?.id]);

	return (
		<FormProvider {...formMethods}>
			<FormContent>
				<FormContainer>
					<form>
						{defaultValues?.id && (
							<TerminalInfo
								terminal={defaultValues}
								isLoading={isDataLoading}
							/>
						)}
						<Origin isLoading={isDataLoading} />
						<hr className={`${styles.divider} mt-2 mb-3`} />
						<Program
							isLoading={isDataLoading}
							processorId={processor?.id}
							isTidVNumberFromApi={
								fileBuild?.isIntegratedWithTsys
							}
							achProcessor={merchantInfo?.achProcessor}
						/>
						<hr className={`${styles.divider} mt-2 mb-3`} />
						<ZeroCost isLoading={isDataLoading} />
						<hr className={`${styles.divider} mt-2 mb-1`} />
						{processor?.id === Processors.TSYS.value && (
							<div className={styles.section}>
								<HookFormCheckbox
									register={register}
									name="isFileBuildOnly"
									id="isFileBuildOnly"
									label="File Build Only (No equipment identified)"
									onChange={checkboxChangeHandler}
									blurLoading={isDataLoading}
								/>
								<p className={styles.warningSing}>
									Only select if you require a Stage Only VAR
									without indicating Terminal, Gateway /<br />
									Virtual Terminal or POS / Software
									information
								</p>
								<hr className={styles.divider} />
							</div>
						)}

						<div className={styles.section}>
							<HookFormCheckbox
								register={register}
								name="isTerminal"
								id="isTerminal"
								label="Terminal"
								onChange={checkboxChangeHandler}
								className="mb-4"
								blurLoading={isDataLoading}
							/>
							{isTerminal && (
								<Terminal
									isLoading={isDataLoading}
									merchantInfo={merchantInfo}
									defaultValues={defaultValues}
									backEndPlatformType={backEndPlatformType}
								/>
							)}
							<hr className={styles.divider} />
						</div>
						<div className={styles.section}>
							<HookFormCheckbox
								register={register}
								name="isGateway"
								id="isGateway"
								label="Gateway"
								onChange={checkboxChangeHandler}
								className="mb-4"
								blurLoading={isDataLoading}
							/>
							{isGateway && (
								<div className={styles.section}>
									<Gateway
										isLoading={isDataLoading}
										merchantInfo={merchantInfo}
										merchantFees={merchantFees}
										backEndPlatformType={
											backEndPlatformType
										}
										fileBuildSectionName={
											FILE_BUILD_SECTION_NAME
										}
									/>
								</div>
							)}
							<hr className={styles.divider} />
						</div>
						<div className={styles.section}>
							<HookFormCheckbox
								register={register}
								name="isPointOfSale"
								id="isPointOfSale"
								label="Point of Sale / Software"
								onChange={checkboxChangeHandler}
								className="mb-4"
								blurLoading={isDataLoading}
							/>
							{isPointOfSale && (
								<PointOfSale
									isLoading={isDataLoading}
									backEndPlatformType={backEndPlatformType}
									defaultValues={defaultValues?.pointOfSale}
									showMarketingServices={isInsideSales}
								/>
							)}
							<hr className={styles.divider} />
						</div>

						{!isPointOfSale && (
							<>
								<div className={styles.subHeader}>
									File Build Information
								</div>
								<FileBuildInfo
									sectionName={FILE_BUILD_SECTION_NAME}
									isLoading={isDataLoading}
									onSubmitStageOnlyVar={
										handleSubmitStageOnlyVar
									}
									setIsCreationStageOnlyVar={
										setIsCreationStageOnlyVar
									}
									refetchTerminalData={refetchTerminalData}
									fileBuild={fileBuild}
								/>
								<hr className={styles.divider} />
							</>
						)}
						<InstallationInfo isLoading={isDataLoading} />
						<InstallationContact
							isLoading={isDataLoading}
							merchantInfo={merchantInfo}
						/>
						{defaultValues?.id && (
							<>
								<hr className={styles.divider} />
								<div className={styles.subHeader}>
									Additional Information
								</div>

								<AdditionalInfo isLoading={isDataLoading} />
							</>
						)}
						<FabList>
							{isDirty &&
								(defaultValues?.status ===
								TerminalStatusTypes.Complete.name ? (
									<Button
										onClick={handleSubmit(
											onUpdateOrComplete,
										)}
										value="Update"
										icon={<SaveIcon />}
										size="lg"
										shape="circle"
										disabled={
											!isDirty || isCreationStageOnlyVar
										}
									/>
								) : (
									<>
										<Button
											onClick={onSubmitDraft}
											value="Draft"
											icon={<SaveIcon />}
											size="lg"
											shape="circle"
											disabled={isCreationStageOnlyVar}
										/>
										<Button
											onClick={handleSubmit(
												onUpdateOrComplete,
											)}
											value="Complete"
											icon={<CheckIcon />}
											size="lg"
											shape="circle"
											variant="success"
											style={{ fontSize: "0.6rem" }}
											disabled={
												!isDirty ||
												isCreationStageOnlyVar
											}
										/>
									</>
								))}
							{isAriseBoardingAvailable && (
								<Button
									onClick={handleAriseBoarding}
									value="Add to ARISE"
									icon={<SubmitIcon />}
									size="lg"
									shape="circle"
									disabled={isDataLoading}
								/>
							)}
						</FabList>
					</form>
				</FormContainer>
			</FormContent>
		</FormProvider>
	);
};

export default TerminalSolutionForm;
